import { useState, useMemo, useEffect, useCallback } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useAccounts } from '@/api/hooks/useAccounts'
import { useCreateCampaign, useFolderUsage } from '@/api/hooks/usePoster'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  Instagram, Facebook, Search, CalendarPlus, Loader2, CheckCheck,
  ChevronRight, ChevronLeft, FolderOpen, Sun, Moon, List, AlertTriangle,
} from 'lucide-react'
import { useLanguage } from '@/i18n/LanguageContext'

interface QuickScheduleDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  folders: Array<{ id: string; name: string; clipCount?: number; imageCount?: number }>
  cutMode?: boolean
}

// ── helpers ──

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + 'T12:00:00')
  d.setDate(d.getDate() + days)
  return d.toISOString().split('T')[0]
}

function formatDateBR(dateStr: string): string {
  const [, m, d] = dateStr.split('-')
  return `${d}/${m}`
}

function todayString(): string {
  return new Date().toISOString().split('T')[0]
}

function weekdayName(dateStr: string): string {
  const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']
  const d = new Date(dateStr + 'T12:00:00')
  return DAYS[d.getDay()]
}

const FOLDER_COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-500' },
  { bg: 'bg-emerald-500', text: 'text-emerald-500' },
  { bg: 'bg-purple-500', text: 'text-purple-500' },
  { bg: 'bg-orange-500', text: 'text-orange-500' },
  { bg: 'bg-pink-500', text: 'text-pink-500' },
  { bg: 'bg-cyan-500', text: 'text-cyan-500' },
]

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab']

// ── Mini Calendar ──

function MiniCalendar({
  selectedDates,
  onToggleDate,
  maxSelections,
  month,
  onMonthChange,
}: {
  selectedDates: string[]
  onToggleDate: (dateStr: string) => void
  maxSelections: number
  month: Date
  onMonthChange: (d: Date) => void
}) {
  const today = todayString()
  const year = month.getFullYear()
  const m = month.getMonth()

  const firstDayOfMonth = new Date(year, m, 1).getDay()
  const daysInMonth = new Date(year, m + 1, 0).getDate()

  const prevMonth = () => onMonthChange(new Date(year, m - 1, 1))
  const nextMonth = () => onMonthChange(new Date(year, m + 1, 1))

  const dateToIndex = useMemo(() => {
    const map: Record<string, number> = {}
    selectedDates.forEach((d, i) => { if (d) map[d] = i })
    return map
  }, [selectedDates])

  const cells: Array<{ day: number; dateStr: string } | null> = []
  for (let i = 0; i < firstDayOfMonth; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const dateStr = `${year}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ day: d, dateStr })
  }

  const filledCount = selectedDates.filter(d => !!d).length

  return (
    <div className="select-none">
      <div className="mb-2 sm:mb-3 flex items-center justify-between">
        <button type="button" onClick={prevMonth} className="rounded-md p-1 sm:p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-xs sm:text-sm font-semibold">{MONTH_NAMES[m]} {year}</span>
        <button type="button" onClick={nextMonth} className="rounded-md p-1 sm:p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-0.5 sm:mb-1 grid grid-cols-7 text-center">
        {WEEKDAYS.map(wd => (
          <span key={wd} className="text-[9px] sm:text-[10px] font-medium text-muted-foreground py-0.5 sm:py-1">{wd}</span>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={`e-${idx}`} />

          const isPast = cell.dateStr < today
          const folderIdx = dateToIndex[cell.dateStr]
          const isSelected = folderIdx !== undefined
          const isFull = filledCount >= maxSelections
          const isDisabled = isPast || (!isSelected && isFull)
          const color = isSelected ? FOLDER_COLORS[folderIdx % FOLDER_COLORS.length] : null
          const isToday = cell.dateStr === today

          return (
            <button
              key={cell.dateStr}
              type="button"
              disabled={isDisabled}
              onClick={() => onToggleDate(cell.dateStr)}
              className={cn(
                'relative flex h-7 sm:h-9 w-full items-center justify-center rounded-md sm:rounded-lg text-[11px] sm:text-xs font-medium transition-all',
                isPast && 'text-muted-foreground/30 cursor-not-allowed',
                !isPast && !isSelected && !isFull && 'hover:bg-muted cursor-pointer text-foreground',
                !isPast && !isSelected && isFull && 'text-muted-foreground/50 cursor-not-allowed',
                isSelected && color && `${color.bg} text-white shadow-sm cursor-pointer`,
                isToday && !isSelected && 'ring-2 ring-primary/30',
              )}
            >
              {cell.day}
              {isSelected && (
                <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3 sm:h-3.5 sm:w-3.5 items-center justify-center rounded-full bg-white text-[7px] sm:text-[8px] font-bold text-foreground shadow-sm">
                  {folderIdx + 1}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Folder Legend ──

function FolderLegend({
  folders,
  dates,
  t,
}: {
  folders: Array<{ id: string; name: string; clipCount?: number; imageCount?: number }>
  dates: string[]
  t: (s: string) => string
}) {
  return (
    <div className="space-y-1 rounded-lg border border-border p-2.5 sm:p-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
        {t('Distribuicao')}
      </p>
      <div className="max-h-[140px] sm:max-h-[200px] overflow-y-auto space-y-1 sm:space-y-1.5">
        {folders.map((folder, i) => {
          const color = FOLDER_COLORS[i % FOLDER_COLORS.length]
          const date = dates[i]
          const clipCount = folder.clipCount || 0
          const half = Math.ceil(clipCount / 2)
          return (
            <div key={folder.id} className="flex items-center gap-1.5 sm:gap-2">
              <span className={cn('flex h-4 w-4 sm:h-5 sm:w-5 shrink-0 items-center justify-center rounded text-[9px] sm:text-[10px] font-bold text-white', color.bg)}>
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[11px] sm:text-xs font-medium">{folder.name}</p>
                {clipCount > 0 && (
                  <p className="text-[9px] sm:text-[10px] text-muted-foreground">
                    {clipCount} clips ({half}+{clipCount - half})
                  </p>
                )}
              </div>
              {date ? (
                <span className={cn('shrink-0 rounded px-1.5 sm:px-2 py-0.5 text-[9px] sm:text-[10px] font-semibold text-white', color.bg)}>
                  {weekdayName(date)} {formatDateBR(date)}
                </span>
              ) : (
                <span className="shrink-0 text-[9px] sm:text-[10px] text-muted-foreground/60 italic">
                  {t('Selecione...')}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Time Slots Config ──

function TimeSlotsConfig({
  morningTime,
  afternoonTime,
  onMorningChange,
  onAfternoonChange,
  t,
}: {
  morningTime: string
  afternoonTime: string
  onMorningChange: (v: string) => void
  onAfternoonChange: (v: string) => void
  t: (s: string) => string
}) {
  return (
    <div className="rounded-lg border border-border p-2.5 sm:p-3">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5 sm:mb-2">
        {t('Horarios dos lotes')}
      </p>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div>
          <label className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-xs font-medium mb-1">
            <Sun className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-500" /> {t('1º Lote')}
          </label>
          <Input type="time" value={morningTime} onChange={(e) => onMorningChange(e.target.value)} className="h-8 sm:h-9 text-xs sm:text-sm" />
        </div>
        <div>
          <label className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-xs font-medium mb-1">
            <Moon className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-indigo-500" /> {t('2º Lote')}
          </label>
          <Input type="time" value={afternoonTime} onChange={(e) => onAfternoonChange(e.target.value)} className="h-8 sm:h-9 text-xs sm:text-sm" />
        </div>
      </div>
      <p className="mt-1.5 text-[9px] sm:text-[10px] text-muted-foreground text-center">
        {t('Metade dos clips em cada horario')}
      </p>
    </div>
  )
}

// ── Dialog ──

export function QuickScheduleDialog({ open, onOpenChange, folders, cutMode }: QuickScheduleDialogProps) {
  const { t } = useLanguage()
  const { data: accounts = [] } = useAccounts()
  const createCampaign = useCreateCampaign()
  const folderIds = useMemo(() => open ? folders.map(f => f.id) : [], [folders, open])
  const { data: folderUsage } = useFolderUsage(folderIds)

  // Shared state
  const [step, setStep] = useState<1 | 2>(1)
  const [selectedAccounts, setSelectedAccounts] = useState<Record<string, { ig: boolean; fb: boolean }>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Library mode state
  const [startDate, setStartDate] = useState(todayString())
  const [startTime, setStartTime] = useState('08:00')
  const [endTime, setEndTime] = useState('20:00')
  const [intervalMinutes, setIntervalMinutes] = useState(30)
  const [postType, setPostType] = useState<'feed' | 'story'>('feed')
  const [distributionMode, setDistributionMode] = useState<'sameDay' | 'multiDay'>('sameDay')
  const [postsPerDay, setPostsPerDay] = useState(1)
  const [multiDayPostTime, setMultiDayPostTime] = useState('10:00')

  // Cut mode state
  const [useBatchMode, setUseBatchMode] = useState(false)
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [calendarMonth, setCalendarMonth] = useState(() => new Date())
  const [morningTime, setMorningTime] = useState('10:00')
  const [afternoonTime, setAfternoonTime] = useState('14:00')

  // Batch mode state
  const [batchStartDate, setBatchStartDate] = useState(todayString())
  const [skipDays, setSkipDays] = useState(0) // 0 = todo dia, 1 = pula 1, 2 = pula 2

  // Custom caption
  const [customCaption, setCustomCaption] = useState('')

  useEffect(() => {
    if (open) {
      setStep(1)
      setSelectedAccounts({})
      setSearchQuery('')
      setStartDate(todayString())
      setStartTime('08:00')
      setEndTime('20:00')
      setIntervalMinutes(30)
      setPostType('feed')
      setDistributionMode('sameDay')
      setPostsPerDay(1)
      setMultiDayPostTime('10:00')
      setIsSubmitting(false)
      setSelectedDates(new Array(folders.length).fill(''))
      setCalendarMonth(new Date())
      setMorningTime('10:00')
      setAfternoonTime('14:00')
      setUseBatchMode(folders.length > 5)
      setBatchStartDate(todayString())
      setSkipDays(0)
      setCustomCaption('')
    }
  }, [open, folders.length])

  // Compute batch dates
  const batchDates = useMemo(() => {
    if (!useBatchMode) return []
    return folders.map((_, i) => addDays(batchStartDate, i * (1 + skipDays)))
  }, [useBatchMode, batchStartDate, skipDays, folders])

  // The final dates to use for submit
  const finalDates = useBatchMode ? batchDates : selectedDates

  // ── Account selection logic ──

  const filteredAccounts = useMemo(() => {
    return accounts.filter(a => {
      if (!searchQuery) return true
      const q = searchQuery.toLowerCase()
      return a.name?.toLowerCase().includes(q) || a.username?.toLowerCase().includes(q)
    })
  }, [accounts, searchQuery])

  const toggleAccount = (igUserId: string, platform: 'ig' | 'fb') => {
    setSelectedAccounts(prev => {
      const current = prev[igUserId] || { ig: false, fb: false }
      return { ...prev, [igUserId]: { ...current, [platform]: !current[platform] } }
    })
  }

  const selectAll = () => {
    const updated = { ...selectedAccounts }
    filteredAccounts.forEach(a => {
      updated[a.igUserId] = { ig: !a.fbOnly, fb: true }
    })
    setSelectedAccounts(updated)
  }

  const selectedCount = useMemo(() => {
    let count = 0
    for (const v of Object.values(selectedAccounts)) {
      if (v.ig) count++
      if (v.fb) count++
    }
    return count
  }, [selectedAccounts])

  // Compute folder usage warnings for selected accounts
  const folderWarnings = useMemo(() => {
    if (!folderUsage) return []
    // Get selected page IDs
    const selectedPageIds = new Set<string>()
    for (const [igUserId, plats] of Object.entries(selectedAccounts)) {
      if (plats.ig || plats.fb) {
        const acc = accounts.find(a => a.igUserId === igUserId)
        if (acc?.pageId) selectedPageIds.add(acc.pageId)
      }
    }
    if (selectedPageIds.size === 0) return []

    const warnings: Array<{ folderId: string; folderName: string; usages: Array<{ accountName: string; count: number; lastUsed: number }> }> = []

    for (const folder of folders) {
      const usage = folderUsage[folder.id]
      if (!usage) continue
      const usages: Array<{ accountName: string; count: number; lastUsed: number }> = []
      for (const [pageId, entry] of Object.entries(usage)) {
        if (selectedPageIds.has(pageId)) {
          usages.push({ accountName: entry.accountName, count: entry.count, lastUsed: entry.lastUsed })
        }
      }
      if (usages.length > 0) {
        warnings.push({ folderId: folder.id, folderName: folder.name, usages })
      }
    }
    return warnings
  }, [folderUsage, selectedAccounts, accounts, folders])

  // Compute ALL usage for each folder (regardless of account selection)
  const folderAllUsage = useMemo(() => {
    if (!folderUsage) return []
    const result: Array<{ folderId: string; folderName: string; usages: Array<{ accountName: string; count: number; lastUsed: number }> }> = []
    for (const folder of folders) {
      const usage = folderUsage[folder.id]
      if (!usage) continue
      const usages = Object.values(usage).map(entry => ({
        accountName: entry.accountName,
        count: entry.count,
        lastUsed: entry.lastUsed,
      }))
      if (usages.length > 0) {
        result.push({ folderId: folder.id, folderName: folder.name, usages })
      }
    }
    return result
  }, [folderUsage, folders])

  const renderUsageWarnings = () => {
    // Step 2: show warnings filtered by selected accounts
    if (step === 2 && folderWarnings.length > 0) {
      return (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              {t('Conteudo ja utilizado nas contas selecionadas')}
            </span>
          </div>
          {folderWarnings.map(w => (
            <div key={w.folderId} className="pl-5 text-[10px] text-amber-700 dark:text-amber-300">
              <span className="font-medium">{w.folderName}</span>
              {w.usages.map((u, i) => (
                <span key={i}>
                  {' '}&middot; {u.accountName}: {u.count}x
                  <span className="text-amber-600/60 dark:text-amber-400/60"> ({new Date(u.lastUsed).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )
    }
    // Step 1 or no account filter: show all usage
    if (folderAllUsage.length > 0) {
      return (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
              {t('Conteudo ja utilizado')}
            </span>
          </div>
          {folderAllUsage.map(w => (
            <div key={w.folderId} className="pl-5 text-[10px] text-amber-700 dark:text-amber-300">
              <span className="font-medium">{w.folderName}</span>
              {w.usages.map((u, i) => (
                <span key={i}>
                  {' '}&middot; {u.accountName}: {u.count}x
                </span>
              ))}
            </div>
          ))}
        </div>
      )
    }
    return null
    return (
      <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 space-y-1.5">
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
          <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">
            {t('Conteudo ja utilizado')}
          </span>
        </div>
        {folderWarnings.map(w => (
          <div key={w.folderId} className="pl-5 text-[10px] text-amber-700 dark:text-amber-300">
            <span className="font-medium">{w.folderName}</span>
            {w.usages.map((u, i) => (
              <span key={i}>
                {' '}&middot; {u.accountName}: {u.count}x
                <span className="text-amber-600/60 dark:text-amber-400/60"> ({new Date(u.lastUsed).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })})</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    )
  }

  const buildAccountsPayload = () => {
    const result: Array<{ igUserId: string; platforms: string[] }> = []
    for (const [igUserId, platforms] of Object.entries(selectedAccounts)) {
      const plats: string[] = []
      if (platforms.ig) plats.push('ig')
      if (platforms.fb) plats.push('fb')
      if (plats.length > 0) result.push({ igUserId, platforms: plats })
    }
    return result
  }

  // ── Calendar date toggling ──

  const handleToggleDate = useCallback((dateStr: string) => {
    setSelectedDates(prev => {
      const next = [...prev]
      const existingIdx = next.indexOf(dateStr)
      if (existingIdx !== -1) {
        next[existingIdx] = ''
        return next
      }
      const emptyIdx = next.indexOf('')
      if (emptyIdx !== -1) {
        next[emptyIdx] = dateStr
      }
      return next
    })
  }, [])

  // ── Submit ──

  const isMultiFolder = folders.length > 1
  const allDatesReady = cutMode
    ? (useBatchMode ? !!batchStartDate : selectedDates.every(d => !!d))
    : true

  const handleSubmit = async () => {
    const accountsPayload = buildAccountsPayload()
    if (accountsPayload.length === 0) { toast.error(t('Selecione ao menos uma conta')); return }

    if (cutMode && !allDatesReady) {
      toast.error(t('Selecione uma data para cada pasta'))
      return
    }

    setIsSubmitting(true)
    let totalScheduled = 0
    let skippedFolders = 0
    try {
      if (cutMode) {
        const dates = useBatchMode ? batchDates : selectedDates
        for (let i = 0; i < folders.length; i++) {
          const res = await createCampaign.mutateAsync({
            fromLibrary: true,
            folderId: folders[i].id,
            clipMode: true,
            accounts: accountsPayload,
            scheduleDate: dates[i],
            clipMorningTime: morningTime,
            clipAfternoonTime: afternoonTime,
            contentItems: [],
            forceRecycle: true,
            ...(customCaption ? { customCaption } : {}),
          })
          if (res.totalPosts > 0) totalScheduled += res.totalPosts
          else skippedFolders++
        }
      } else if (distributionMode === 'multiDay') {
        for (let i = 0; i < folders.length; i++) {
          const imgCount = folders[i].imageCount || 10
          const days = Math.ceil(imgCount / postsPerDay)
          const folderStartDate = addDays(startDate, i * days)
          const res = await createCampaign.mutateAsync({
            fromLibrary: true,
            folderId: folders[i].id,
            accounts: accountsPayload,
            multiDay: true,
            startDate: folderStartDate,
            totalDays: days,
            postTime: multiDayPostTime,
            imagesPerDay: postsPerDay,
            intervalMin: 30,
            intervalMax: 60,
            contentItems: [],
            includeStories: postType === 'story',
            ...(customCaption ? { customCaption } : {}),
          })
          if (res.totalPosts > 0) totalScheduled += res.totalPosts
          else skippedFolders++
        }
      } else {
        for (let i = 0; i < folders.length; i++) {
          const date = addDays(startDate, i)
          const res = await createCampaign.mutateAsync({
            fromLibrary: true,
            folderId: folders[i].id,
            accounts: accountsPayload,
            scheduleDate: date,
            startTime,
            endTime,
            intervalMinutes,
            contentItems: [],
            includeStories: postType === 'story',
            ...(customCaption ? { customCaption } : {}),
          })
          if (res.totalPosts > 0) totalScheduled += res.totalPosts
          else skippedFolders++
        }
      }

      if (totalScheduled > 0) {
        toast.success(`${totalScheduled} ${t('posts agendados!')}`)
      } else {
        toast.error(t('Nenhum post foi agendado. Verifique se as pastas possuem cortes.'))
      }
      if (skippedFolders > 0 && totalScheduled > 0) {
        toast.warning(`${skippedFolders} ${t('pasta(s) sem cortes encontrados')}`)
      }
      onOpenChange(false)
    } catch (err: any) {
      toast.error(err.message || t('Erro ao agendar'))
    } finally {
      setIsSubmitting(false)
    }
  }

  // ── Render ──

  const renderStep1 = () => (
    <div className="space-y-2.5 sm:space-y-3">
      {renderUsageWarnings()}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('Buscar conta...')}
            className="h-8 pl-8 text-xs"
          />
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1 text-[10px]" onClick={selectAll}>
          <CheckCheck className="h-3 w-3" /> {t('Todas')}
        </Button>
      </div>

      <div className="max-h-[240px] sm:max-h-[300px] space-y-0.5 sm:space-y-1 overflow-y-auto rounded-lg border border-border p-1.5 sm:p-2">
        {filteredAccounts.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">{t('Nenhuma conta vinculada')}</p>
        ) : filteredAccounts.map(account => {
          const sel = selectedAccounts[account.igUserId] || { ig: false, fb: false }
          const anySelected = sel.ig || sel.fb
          return (
            <div
              key={account.igUserId}
              className={cn(
                'flex items-center justify-between rounded-md px-2 py-1 sm:px-2.5 sm:py-1.5 transition-colors',
                anySelected ? 'bg-primary/5' : 'hover:bg-muted/50',
              )}
            >
              <span className="truncate text-[11px] sm:text-xs font-medium mr-2">
                {account.fbOnly ? account.name : `@${account.username}`}
              </span>
              <div className="flex gap-1 sm:gap-1.5 shrink-0">
                {!account.fbOnly && (
                  <button
                    type="button"
                    onClick={() => toggleAccount(account.igUserId, 'ig')}
                    className={cn(
                      'flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9px] font-medium transition-colors',
                      sel.ig
                        ? 'border-purple-500/40 bg-purple-500/15 text-purple-600 dark:text-purple-400'
                        : 'border-border text-muted-foreground hover:border-purple-500/30',
                    )}
                  >
                    <Instagram className="h-2.5 w-2.5" /> IG
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleAccount(account.igUserId, 'fb')}
                  className={cn(
                    'flex items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9px] font-medium transition-colors',
                    sel.fb
                      ? 'border-blue-500/40 bg-blue-500/15 text-blue-600 dark:text-blue-400'
                      : 'border-border text-muted-foreground hover:border-blue-500/30',
                  )}
                >
                  <Facebook className="h-2.5 w-2.5" /> FB
                </button>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] sm:text-xs text-muted-foreground">
          {selectedCount} {t('destino(s)')}
        </span>
        <Button size="sm" className="gap-1 h-8 text-xs" disabled={selectedCount === 0} onClick={() => setStep(2)}>
          {t('Proximo')} <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )

  const renderCutModeStep2 = () => (
    <div className="space-y-3 sm:space-y-4">
      {renderUsageWarnings()}
      {/* Mode toggle for >1 folder */}
      {folders.length > 1 && (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => setUseBatchMode(false)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-medium transition-colors',
              !useBatchMode
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            <CalendarPlus className="h-3.5 w-3.5 shrink-0" /> {t('Calendario')}
          </button>
          <button
            type="button"
            onClick={() => setUseBatchMode(true)}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-medium transition-colors',
              useBatchMode
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border text-muted-foreground hover:bg-muted',
            )}
          >
            <List className="h-3.5 w-3.5 shrink-0" /> {t('Sequencial')}
          </button>
        </div>
      )}

      {useBatchMode ? (
        <>
          {/* Batch config */}
          <div className="rounded-xl border border-border p-2.5 sm:p-3 space-y-2.5 sm:space-y-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t('Configurar sequencia')}
            </p>
            <div>
              <label className="text-xs font-medium">{t('Dia inicial')}</label>
              <Input
                type="date"
                value={batchStartDate}
                onChange={(e) => setBatchStartDate(e.target.value)}
                className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"
                min={todayString()}
              />
            </div>
            <div>
              <label className="text-xs font-medium">{t('Frequencia')}</label>
              <div className="mt-1.5 grid grid-cols-3 gap-1 sm:gap-1.5">
                {[
                  { value: 0, label: 'Todo dia' },
                  { value: 1, label: 'Pular 1' },
                  { value: 2, label: 'Pular 2' },
                ].map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSkipDays(opt.value)}
                    className={cn(
                      'rounded-lg border px-1.5 py-1.5 sm:px-2 sm:py-2 text-[10px] sm:text-xs font-medium transition-colors text-center',
                      skipDays === opt.value
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border text-muted-foreground hover:bg-muted',
                    )}
                  >
                    {t(opt.label)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview list */}
          <FolderLegend folders={folders} dates={batchDates} t={t} />
        </>
      ) : (
        <>
          {/* Calendar */}
          <div className="rounded-xl border border-border p-2.5 sm:p-3">
            <p className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {t('Clique nas datas para cada pasta')}
            </p>
            <MiniCalendar
              selectedDates={selectedDates}
              onToggleDate={handleToggleDate}
              maxSelections={folders.length}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
            />
          </div>

          {/* Legend */}
          <FolderLegend folders={folders} dates={selectedDates} t={t} />
        </>
      )}

      {/* Time slots */}
      <TimeSlotsConfig
        morningTime={morningTime}
        afternoonTime={afternoonTime}
        onMorningChange={setMorningTime}
        onAfternoonChange={setAfternoonTime}
        t={t}
      />

      {/* Custom caption */}
      <div className="rounded-lg border border-border p-2.5 sm:p-3">
        <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
          {t('Legenda personalizada (opcional)')}
        </label>
        <Textarea
          value={customCaption}
          onChange={(e) => setCustomCaption(e.target.value)}
          placeholder={t('Deixe vazio para legenda automatica...')}
          className="mt-1.5 min-h-[44px] text-xs resize-none"
        />
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-1">
        <Button size="sm" variant="ghost" className="gap-1 h-8 text-xs" onClick={() => setStep(1)}>
          <ChevronLeft className="h-3.5 w-3.5" /> {t('Voltar')}
        </Button>
        <Button
          size="sm"
          className="gap-1.5 h-8 text-xs"
          disabled={isSubmitting || !allDatesReady}
          onClick={handleSubmit}
        >
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="h-3.5 w-3.5" />}
          {isSubmitting
            ? t('Agendando...')
            : isMultiFolder
              ? `${t('Agendar')} ${folders.length} ${t('pastas')}`
              : t('Agendar')
          }
        </Button>
      </div>
    </div>
  )

  const totalImages = folders.reduce((sum, f) => sum + (f.imageCount || 0), 0)
  const multiDayTotalDays = postsPerDay > 0 ? Math.ceil((folders[0]?.imageCount || 10) / postsPerDay) : 1
  const multiDayEndDate = addDays(startDate, multiDayTotalDays - 1)

  const renderLibraryStep2 = () => (
    <div className="space-y-3 sm:space-y-4">
      {renderUsageWarnings()}
      {/* Distribution mode toggle */}
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => setDistributionMode('sameDay')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-medium transition-colors',
            distributionMode === 'sameDay'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:bg-muted',
          )}
        >
          <Sun className="h-3.5 w-3.5 shrink-0" /> {t('Tudo no dia')}
        </button>
        <button
          type="button"
          onClick={() => setDistributionMode('multiDay')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 rounded-lg border px-2 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs font-medium transition-colors',
            distributionMode === 'multiDay'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border text-muted-foreground hover:bg-muted',
          )}
        >
          <List className="h-3.5 w-3.5 shrink-0" /> {t('1 por dia')}
        </button>
      </div>

      <div className="space-y-2.5 sm:space-y-3">
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          <div>
            <label className="text-xs font-medium">{t('Data inicio')}</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"
              min={todayString()}
            />
          </div>
          <div>
            <label className="text-xs font-medium">{t('Tipo')}</label>
            <Select value={postType} onValueChange={(v) => setPostType(v as 'feed' | 'story')}>
              <SelectTrigger className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="feed">Feed</SelectItem>
                <SelectItem value="story">Story</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {distributionMode === 'sameDay' ? (
          <>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <label className="text-xs font-medium">{t('Horario inicio')}</label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="mt-1 h-8 sm:h-9 text-xs sm:text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium">{t('Horario fim')}</label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="mt-1 h-8 sm:h-9 text-xs sm:text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium">{t('Intervalo')}</label>
              <Select value={String(intervalMinutes)} onValueChange={(v) => setIntervalMinutes(Number(v))}>
                <SelectTrigger className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 min</SelectItem>
                  <SelectItem value="30">30 min</SelectItem>
                  <SelectItem value="60">1 hora</SelectItem>
                  <SelectItem value="120">2 horas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <label className="text-xs font-medium">{t('Horario do post')}</label>
                <Input type="time" value={multiDayPostTime} onChange={(e) => setMultiDayPostTime(e.target.value)} className="mt-1 h-8 sm:h-9 text-xs sm:text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium">{t('Posts por dia')}</label>
                <Select value={String(postsPerDay)} onValueChange={(v) => setPostsPerDay(Number(v))}>
                  <SelectTrigger className="mt-1 h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 {t('post')}</SelectItem>
                    <SelectItem value="2">2 {t('posts')}</SelectItem>
                    <SelectItem value="3">3 {t('posts')}</SelectItem>
                    <SelectItem value="5">5 {t('posts')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview multi-day distribution */}
            <div className="rounded-lg border border-border p-2.5 sm:p-3">
              <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-1.5">
                {t('Previa')}
              </p>
              <div className="space-y-1 text-[11px] sm:text-xs">
                {folders.map(folder => {
                  const imgs = folder.imageCount || 0
                  const days = postsPerDay > 0 ? Math.ceil(imgs / postsPerDay) : 1
                  const endD = addDays(startDate, days - 1)
                  return (
                    <div key={folder.id} className="flex items-center gap-2">
                      <FolderOpen className="h-3 w-3 text-muted-foreground shrink-0" />
                      <span className="truncate flex-1">{folder.name}</span>
                      <span className="text-muted-foreground shrink-0">
                        {imgs} {t('imgs')} &rarr; {days} {t('dias')}
                      </span>
                    </div>
                  )
                })}
                <p className="text-[10px] text-muted-foreground mt-1">
                  {formatDateBR(startDate)} &rarr; {formatDateBR(multiDayEndDate)} &middot; {postsPerDay}/{t('dia')} &middot; {multiDayPostTime}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {distributionMode === 'sameDay' && isMultiFolder && (
        <div className="space-y-1 sm:space-y-1.5 rounded-lg border border-border p-2.5 sm:p-3">
          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{t('Distribuicao')}</p>
          <div className="max-h-[120px] overflow-y-auto space-y-1">
            {folders.map((folder, i) => (
              <div key={folder.id} className="flex items-center gap-2 text-[11px] sm:text-xs">
                <FolderOpen className="h-3 w-3 text-muted-foreground shrink-0" />
                <span className="truncate flex-1">{folder.name}</span>
                <span className="text-muted-foreground shrink-0">{formatDateBR(addDays(startDate, i))}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom caption */}
      <div>
        <label className="text-xs font-medium">{t('Legenda personalizada (opcional)')}</label>
        <Textarea
          value={customCaption}
          onChange={(e) => setCustomCaption(e.target.value)}
          placeholder={t('Deixe vazio para legenda automatica...')}
          className="mt-1 min-h-[44px] text-xs resize-none"
        />
      </div>

      <div className="flex items-center justify-between pt-1">
        <Button size="sm" variant="ghost" className="gap-1 h-8 text-xs" onClick={() => setStep(1)}>
          <ChevronLeft className="h-3.5 w-3.5" /> {t('Voltar')}
        </Button>
        <Button size="sm" className="gap-1.5 h-8 text-xs" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarPlus className="h-3.5 w-3.5" />}
          {isSubmitting
            ? t('Agendando...')
            : isMultiFolder
              ? `${t('Agendar')} ${folders.length} ${t('pastas')}`
              : t('Agendar')
          }
        </Button>
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-lg p-4 sm:p-6', cutMode && 'max-w-md')}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
            <CalendarPlus className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {cutMode
                ? `${t('Agendar Cortes')}${isMultiFolder ? ` (${folders.length})` : ''}`
                : isMultiFolder
                  ? `${t('Agendar')} ${folders.length} ${t('pastas')}`
                  : `${t('Agendar')} "${folders[0]?.name}"`
              }
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[calc(85vh-5rem)] overflow-y-auto -mx-4 px-4 sm:-mx-6 sm:px-6">
          {step === 1
            ? renderStep1()
            : cutMode
              ? renderCutModeStep2()
              : renderLibraryStep2()
          }
        </div>
      </DialogContent>
    </Dialog>
  )
}
